/**
 * customsirge_stay_frequency.js
 *
 * Purpose: renders the Stay AI subscription frequency picker as a horizontal button group in the
 *          top-right of the "Subscribe & Save" row, per Figma 28886:7108 (group 28886:7116),
 *          replacing the app's full-width native <select>.
 * Pairs with: the scoped `customsirge: Stay AI` rules in sections/main-product.liquid's
 *          {%- style -%} block, which hide the tag, clip the select and style these buttons.
 * Owner:   figma-team / 2026-08-18
 *
 * WHY A SHIM AND NOT A TEMPLATE EDIT: the widget is an app block
 * (shopify://apps/stay-ai-subscription/blocks/buy-box-extended), rendered by a bare
 * `{% render block %}`, so its markup cannot be edited from the theme.
 *
 * WHY THE <select> SURVIVES: probing the live storefront showed the app never reacts to a
 * `change` event — price, ATC label and DOM stay byte-identical. It reads `select.value` at
 * submit time. So the select must remain a live, in-DOM form control with its value in sync; the
 * CSS clips it rather than removing it.
 *
 * WHY `desiredPlan` EXISTS — the load-bearing detail. Roughly 90ms after a click the app
 * asynchronously REBUILDS the select's <option> list (measured: value flips to the user's choice
 * at t+114ms, then the list is replaced at t+207ms with selectedIndex back to 0 and the
 * "Select frequency" placeholder dropped). That silently discarded the user's pick, and no
 * same-tick fix could catch it — a requestAnimationFrame re-assert ran ~90ms too early.
 * So the user's choice is held here as the source of truth and re-applied whenever the app
 * rewrites the options. Timing-independent by construction, rather than a tuned setTimeout.
 *
 * WHERE THE BUTTONS GO: into `.stayai-purchase-label__inner__wrapper`, which the app's own
 * stylesheet already declares `display:flex; align-items:center`. `margin-left:auto` then parks
 * the group at the right edge, vertically centred, with no absolute positioning — so it cannot
 * overlap the label, and mobile only needs `flex-wrap`.
 */
(function () {
  if (window.__customsirgeStayFrequency) return;
  window.__customsirgeStayFrequency = true;

  var SELECT = '.stayai-subscription-dropdown';
  var GROUP = 'data-stayai-freq';

  /** The plan the shopper actually chose. Survives the app's option rebuilds. */
  var desiredPlan = null;

  function plansOf(select) {
    // The app emits a value="" "Select frequency" placeholder; it is not a real plan. It also
    // drops that placeholder on rebuild, which is why selection is tracked by value, never index.
    return Array.prototype.filter.call(select.options, function (o) {
      return o.value !== '';
    });
  }

  function hasPlan(select, value) {
    return Array.prototype.some.call(select.options, function (o) {
      return o.value === value;
    });
  }

  /** Paint aria-checked from whatever the select currently holds. */
  function sync(group, select) {
    Array.prototype.forEach.call(group.querySelectorAll('button[data-plan]'), function (btn) {
      btn.setAttribute('aria-checked', String(btn.getAttribute('data-plan') === select.value));
    });
  }

  /**
   * Re-assert the shopper's plan if anything (i.e. the app's option rebuild) has moved it.
   * Deliberately yields in two cases so we never fight a legitimate app decision:
   *  - the shopper explicitly picked One-Time, so the app clearing the plan is correct;
   *  - the chosen plan no longer exists for this variant/product.
   */
  function enforce(select) {
    if (!desiredPlan) return;

    var oneTime = document.getElementById('purchaseTypeOneTime');
    if (oneTime && oneTime.checked) {
      desiredPlan = null;
      return;
    }
    if (!hasPlan(select, desiredPlan)) {
      desiredPlan = null;
      return;
    }
    if (select.value !== desiredPlan) {
      select.value = desiredPlan;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function build(select) {
    var wrapper = select.closest('.stayai-purchase-label__wrapper');
    // Resolve the host row via the select rather than the `purchaseTypeSubscription` id, so a
    // renamed or duplicated id does not break placement.
    var row = wrapper && wrapper.querySelector('.stayai-purchase-label__inner__wrapper');
    if (!row) return;

    var plans = plansOf(select);
    if (!plans.length) return;

    var signature = plans
      .map(function (o) {
        return o.value + ':' + o.textContent.trim();
      })
      .join('|');

    var existing = row.querySelector('[' + GROUP + ']');

    // Idempotent: if the same plans are already rendered, just correct and repaint. This is what
    // keeps the document-wide observer from looping on its own insertions.
    if (existing && existing.getAttribute('data-plans') === signature) {
      enforce(select);
      sync(existing, select);
      return;
    }
    if (existing) existing.remove();
    // A genuinely different plan set means a different product/variant; the old choice is void.
    if (existing) desiredPlan = null;

    var group = document.createElement('div');
    group.setAttribute(GROUP, '');
    group.setAttribute('data-plans', signature);
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', 'Delivery frequency');

    plans.forEach(function (option) {
      var value = option.value;
      var btn = document.createElement('button');
      // type=button matters: the group sits inside <label for="purchaseTypeSubscription">, and a
      // submit-type button here would post the product form.
      btn.type = 'button';
      btn.setAttribute('data-plan', value);
      btn.setAttribute('role', 'radio');
      btn.textContent = option.textContent.trim();

      btn.addEventListener('click', function (event) {
        // Stop the wrapping <label> forwarding this click to its radio and toggling purchase type.
        event.preventDefault();

        desiredPlan = value;

        // The label no longer acts, so adopt its one useful side effect: picking a frequency
        // implies subscribing.
        var radio = document.getElementById('purchaseTypeSubscription');
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync(group, select);
        // The app's async option rebuild is handled by enforce() via the observers below.
      });

      group.appendChild(btn);
    });

    row.appendChild(group);
    enforce(select);
    sync(group, select);

    // The buttons are the control now; keep the clipped select out of the tab order so there is
    // no invisible second stop.
    select.setAttribute('aria-hidden', 'true');
    select.setAttribute('tabindex', '-1');

    if (!select.__customsirgeBound) {
      select.__customsirgeBound = true;

      // The app rewrites <option> children asynchronously — this is the hook that makes the
      // shopper's choice stick.
      new MutationObserver(function () {
        enforce(select);
        var g = row.querySelector('[' + GROUP + ']');
        if (g) sync(g, select);
      }).observe(select, { childList: true });

      // Covers the app (or anything else) changing the plan behind our back.
      select.addEventListener('change', function () {
        var g = row.querySelector('[' + GROUP + ']');
        if (g) sync(g, select);
      });

      // An explicit One-Time choice retires the remembered plan.
      var oneTime = document.getElementById('purchaseTypeOneTime');
      if (oneTime) {
        oneTime.addEventListener('change', function () {
          if (oneTime.checked) desiredPlan = null;
        });
      }
    }
  }

  function run() {
    var select = document.querySelector(SELECT);
    if (select) build(select);
  }

  run();

  // The widget hydrates client-side and re-renders on variant change, which would otherwise
  // orphan the buttons — so this observer stays connected for the life of the page rather than
  // disconnecting after the first hit. Same idiom as the custom_qty block in main-product.liquid.
  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      run();
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
